import React from "react";

import type { MultiBrowseBlock as Value } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { Themed } from "../../styles/themes";
import { Clickable } from "../Clickables";
import { ProductCard } from "../ProductCard";

import styles from "./Slides.module.scss";

type Slide = NonNullable<Value["slides"]>[number];
type CaptionSlideValue = Extract<Slide, { type: "caption" }>["value"];
type TextSlideValue = Extract<Slide, { type: "text" }>["value"];
type ProductSlideValue = Extract<Slide, { type: "product" }>["value"];

export const SlideLink = ({
    link,
    children,
    className,
}: {
    link: CaptionSlideValue["link"];
    children: React.ReactNode;
    className?: string;
}) => {
    const href = link?.url || link?.anchor;
    if (!href) return <>{children}</>;
    return (
        <Clickable href={href} className={className ?? styles.clickable}>
            {children}
        </Clickable>
    );
};

export const CaptionSlide = ({
    value,
    imageSizes,
}: {
    value: CaptionSlideValue;
    imageSizes?: string;
}) => {
    if (!value.image) return null;
    return (
        <Themed theme="darkThemeText" tag="figure" className={styles.slide}>
            <SlideLink link={value.link}>
                <ImageChooserBlock
                    attrs={{ className: styles.imageContainer }}
                    value={value.image}
                    sizes={imageSizes}
                    imageAttrs={{ className: styles.image, alt: "" }}
                />
                <figcaption className={styles.caption}>
                    <span className={styles.captionLabel}>{value.label}</span>
                </figcaption>
            </SlideLink>
        </Themed>
    );
};

export const TextSlide = ({
    value,
    imageSizes,
}: {
    value: TextSlideValue;
    imageSizes?: string;
}) => {
    if (!value.image) return null;

    const HeadingTag = value.heading_tag ?? "h3";

    return (
        <Themed theme="darkThemeText" className={styles.slide}>
            <SlideLink link={value.link}>
                <ImageChooserBlock
                    attrs={{ className: styles.imageContainer }}
                    value={value.image}
                    sizes={imageSizes}
                    imageAttrs={{ className: styles.image, alt: "" }}
                />
                <div className={styles.textContent}>
                    {value.big_text ? (
                        <>
                            <HeadingTag className={styles.bigText}>
                                {value.big_text}
                            </HeadingTag>
                            <p className={styles.textHeading}>
                                {value.heading}
                            </p>
                        </>
                    ) : (
                        <HeadingTag className={styles.textHeading}>
                            {value.heading}
                        </HeadingTag>
                    )}
                    {value.body && (
                        <p className={styles.textBody}>{value.body}</p>
                    )}
                </div>
            </SlideLink>
        </Themed>
    );
};

export const ProductSlide = ({ value }: { value: ProductSlideValue }) => {
    if (!value) return null;
    return <ProductCard product={value} layout="vertical" />;
};
